// Recordings waiting to be dealt with, as two buttons beside SyncTo.
//
// This replaces a scattering of one-off surfaces — a prompt on page load for
// recordings that never uploaded, a modal for transcriptions that failed, and
// two groups tucked inside the note rail. Each of those knew about one kind of
// problem and appeared at a moment the product chose. What a practitioner
// actually needs is a standing answer to two questions: what have I recorded
// that isn't written up, and what did I throw away.
//
//   Review Needed — everything still in flight: transcribing, ready to review,
//                   failed, and recordings whose audio never left this browser.
//   Dismissed     — what was thrown away, still restorable for 7 days.
//
// Nothing here writes. Applying happens in ReviewModal (which creates the note
// via /apply, so the dashboard review task closes with it); this component only
// decides what to show and what each row does when clicked.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Dropdown, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { useRecording } from './RecordingProvider';
import { useSyncToApi } from './hooks/useSyncToApi';
import DismissedCapturesModal from './DismissedCapturesModal';

/** Statuses that mean "the server is still working on it". */
const IN_FLIGHT = new Set(['QUEUED', 'PROCESSING']);

const fmtDate = (value) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

/**
 * One row's worth of display, for a server capture or a local recording.
 *
 * Local rows are deliberately marked: they live in this browser only, so a
 * colleague looking at the same patient won't see them, and saying so is
 * better than implying the recording is safely filed away somewhere.
 */
function describe(row) {
  if (row.kind === 'local') {
    return {
      title: row.patientLabel || 'Recording',
      detail: 'Not sent — held on this device',
      icon: 'fa-light fa-cloud-arrow-up',
      tone: 'warning',
    };
  }
  const kind = row.mode === 'template' ? 'Template recording' : 'Recording';
  if (row.status === 'FAILED') {
    return {
      title: kind,
      detail: row.error ? `Failed — ${row.error}` : "Transcription didn't complete",
      icon: 'fa-light fa-triangle-exclamation',
      tone: 'danger',
    };
  }
  if (IN_FLIGHT.has(row.status)) {
    return { title: kind, detail: 'Transcribing…', icon: 'fa-light fa-hourglass-half', tone: 'muted' };
  }
  return { title: kind, detail: 'Ready to review', icon: 'fa-light fa-waveform-lines', tone: 'primary' };
}

export default function CaptureReviewBar({
  patientId,
  width,
  // Drop the button labels and rely on icon + tooltip. Used where the two sit
  // in a labelled SyncTo group, so the group's own label carries the meaning
  // and repeating it on every button only makes the row longer.
  iconOnly = false,
  height,
  // Called after anything changes that the surrounding page may want to reload
  // (a capture applied, dismissed, restored or sent).
  onChanged,
}) {
  const recording = useRecording();
  const { listJobs, dismissJob, restoreJob } = useSyncToApi();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [dismissedOpen, setDismissedOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    const { data, error: err } = await listJobs({ patientId });
    setLoading(false);
    if (err) {
      // Surfaced rather than swallowed: an empty list and a failed request look
      // identical otherwise, which is how a 403 from the feature flag used to
      // read as "nothing waiting".
      setError(err.message || 'Could not load recordings');
      setJobs([]);
      return;
    }
    setError(null);
    setJobs(Array.isArray(data?.jobs) ? data.jobs : []);
  }, [patientId, listJobs]);

  useEffect(() => { refresh(); }, [refresh]);

  // An apply or a dismiss anywhere on this patient changes both lists — a
  // capture leaves Review Needed either way, and a dismiss puts it in
  // Dismissed. Both events are listened for; missing the dismiss one is why
  // the Dismissed list used to need a page reload to show what had just been
  // thrown away.
  useEffect(() => {
    const handler = (e) => {
      const detail = (e && e.detail) || {};
      if (detail.patientId && String(detail.patientId) !== String(patientId)) return;
      refresh();
    };
    window.addEventListener('syncto:applied', handler);
    window.addEventListener('syncto:dismissed', handler);
    // A fresh upload also changes Review Needed: the capture arrives there as
    // an in-flight row, and the page may already be showing this bar when it
    // does (recording started from Previous Notes itself).
    window.addEventListener('syncto:uploaded', handler);
    return () => {
      window.removeEventListener('syncto:applied', handler);
      window.removeEventListener('syncto:dismissed', handler);
      window.removeEventListener('syncto:uploaded', handler);
    };
  }, [patientId, refresh]);

  // Local, un-sent recordings sit alongside the server's, oldest concern first.
  const localRows = useMemo(
    () => (recording?.orphans || []).map((o) => ({
      kind: 'local',
      _id: o.sessionKey,
      sessionKey: o.sessionKey,
      patientLabel: o.patientLabel,
      createdAt: o.startedAt,
      sendable: !!(o.patientId && o.practiceId),
    })),
    [recording?.orphans],
  );

  const reviewRows = useMemo(() => {
    const server = jobs
      .filter((j) => !j.recoverable)
      .map((j) => ({ ...j, kind: 'job' }));
    return [...server, ...localRows].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
    );
  }, [jobs, localRows]);

  const dismissedRows = useMemo(() => jobs.filter((j) => j.recoverable), [jobs]);

  const after = useCallback(async () => {
    await refresh();
    onChanged?.();
  }, [refresh, onChanged]);

  const onOpen = useCallback((row) => {
    if (row.kind === 'local' || IN_FLIGHT.has(row.status) || row.status === 'FAILED') return;
    // `force` — an explicit click must open the review even when this capture
    // was surfaced automatically earlier in the session.
    recording?.openJobForReview?.(row._id, { force: true });
  }, [recording]);

  const onSend = useCallback(async (row) => {
    setBusyId(row._id);
    const res = await recording?.sendOrphan?.(row.sessionKey);
    setBusyId(null);
    if (res && !res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    after();
  }, [recording, after]);

  const onDiscardLocal = useCallback(async (row) => {
    setBusyId(row._id);
    await recording?.discardOrphan?.(row.sessionKey);
    setBusyId(null);
    after();
  }, [recording, after]);

  const onDismiss = useCallback(async (row) => {
    setBusyId(row._id);
    await dismissJob(row._id);
    setBusyId(null);
    after();
  }, [dismissJob, after]);

  const onRestore = useCallback(async (row) => {
    const { error: err } = await restoreJob(row._id);
    if (err) setError(err.message || 'This recording is no longer recoverable.');
    after();
  }, [restoreJob, after]);

  const reviewCount = reviewRows.length;
  const dismissedCount = dismissedRows.length;
  // Labels stay on everywhere, mobile included. Icon-only buttons here left
  // Review Needed and Dismissed indistinguishable without hovering, and the
  // toolbar they sit in wraps, so the words cost a wrap rather than an overflow.
  const hideLabels = iconOnly;
  // Applied in icon-only mode too: the caller sizes all the SyncTo buttons to
  // one width so they read as a set, and a button that shrank to fit its glyph
  // would be the odd one out. Wide enough for icon + count badge.
  const btnWidth = width ? { width } : undefined;
  // Height is imposed for the same reason the width is: the count badge is
  // taller than the icon it sits beside, so these two buttons grew past a
  // plain icon button the moment they had a count to show.
  const btnHeight = height ? { height } : undefined;
  const btnBox = { ...btnWidth, ...btnHeight };

  return (
    <>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>Review Needed{reviewCount > 0 ? ` (${reviewCount})` : ''}</Tooltip>}
      >
      <Dropdown style={btnWidth}>
        <Dropdown.Toggle
          variant={reviewCount > 0 ? 'primary' : 'outline-primary'}
          className="rounded-0 d-flex align-items-center gap-2 justify-content-center w-100"
          disabled={reviewCount === 0 && !error}
          style={btnHeight}
          id="capture-review-toggle"
          aria-label="Review Needed"
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <i className="fa-light fa-waveform-lines" />
          )}
          {!hideLabels && <span>Review Needed</span>}
          {reviewCount > 0 && (
            <Badge bg="light" text="dark">{reviewCount}</Badge>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu className="shadow-sm" style={{ minWidth: 340, maxHeight: 360, overflowY: 'auto' }}>
          {!!error && (
            <div className="px-3 py-2 small text-danger">{error}</div>
          )}
          {reviewCount === 0 && !error && (
            <div className="px-3 py-2 small text-muted">Nothing waiting.</div>
          )}
          {reviewRows.map((row) => {
            const d = describe(row);
            const busy = busyId === row._id;
            const openable = row.kind === 'job' && row.status === 'COMPLETE';
            return (
              <div
                key={row._id}
                className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
              >
                <i className={`${d.icon} text-${d.tone === 'muted' ? 'muted' : d.tone}`} />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-primary text-truncate">{d.title}</div>
                  <div className="small text-muted text-truncate">
                    {fmtDate(row.createdAt)} · {d.detail}
                  </div>
                </div>
                {openable && (
                  <Button size="sm" variant="primary" onClick={() => onOpen(row)}>
                    Review
                  </Button>
                )}
                {row.kind === 'local' && (
                  row.sendable ? (
                    <Button size="sm" variant="primary" disabled={busy} onClick={() => onSend(row)}>
                      {busy ? <Spinner animation="border" size="sm" /> : 'Send'}
                    </Button>
                  ) : (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Recorded before automatic sending — download it instead</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => recording?.downloadOrphan?.(row.sessionKey)}
                      >
                        Download
                      </Button>
                    </OverlayTrigger>
                  )
                )}
                <OverlayTrigger placement="top" overlay={<Tooltip>Discard</Tooltip>}>
                  <Button
                    variant="link"
                    className="text-danger p-0 px-1 border-0"
                    aria-label="Discard recording"
                    disabled={busy}
                    onClick={() => (row.kind === 'local' ? onDiscardLocal(row) : onDismiss(row))}
                  >
                    <i className="fa-solid fa-trash" style={{ fontSize: '0.75rem' }} />
                  </Button>
                </OverlayTrigger>
              </div>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
      </OverlayTrigger>

      {/* A disabled button swallows its own mouse events, so the tooltip has to
          hang off a wrapper or it never fires on the empty state — which is
          exactly when "what is this?" needs answering. */}
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>Dismissed{dismissedCount > 0 ? ` (${dismissedCount})` : ''}</Tooltip>}
      >
        <span className={dismissedCount === 0 ? 'd-inline-block' : undefined}>
          <Button
            variant="outline-secondary"
            className="rounded-0 d-flex align-items-center gap-2 justify-content-center"
            style={btnBox}
            disabled={dismissedCount === 0}
            onClick={() => setDismissedOpen(true)}
            aria-label="Dismissed recordings"
          >
            <i className="fa-solid fa-rotate-left" />
            {!hideLabels && <span>Dismissed</span>}
            {dismissedCount > 0 && <Badge bg="light" text="dark">{dismissedCount}</Badge>}
          </Button>
        </span>
      </OverlayTrigger>

      <DismissedCapturesModal
        show={dismissedOpen}
        onHide={() => setDismissedOpen(false)}
        captures={dismissedRows}
        onRestore={onRestore}
      />
    </>
  );
}
